import { useState } from "react";
import { X, MapPin, Search, ChevronLeft, ArrowRight, Baby, Calendar, Clock, Users, ExternalLink } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useFindAClass } from "../context/FindAClassContext";
import { useBookingLinks, getBookingUrl } from "../context/BookingLinksContext";
import { useSiteImages } from "../context/SiteImagesContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const YEARS = [];
for (let y = currentYear; y >= currentYear - 6; y--) {
  YEARS.push(y);
}
const CUTOFF_YEAR = currentYear - 7;

const getDaysInMonth = (month, year) => {
  return new Date(year, month + 1, 0).getDate();
};

const FindAClassModal = () => {
  const { isOpen, closeFindAClass } = useFindAClass();
  const bookingLinks = useBookingLinks();
  const siteImages = useSiteImages();
  const [step, setStep] = useState("year");
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [postcode, setPostcode] = useState("");
  const [showDynamo, setShowDynamo] = useState(false);
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const reset = () => {
    setStep("year");
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    setPostcode("");
    setShowDynamo(false);
    setResults(null);
    setIsLoading(false);
  };

  const handleClose = () => {
    closeFindAClass();
    setTimeout(reset, 300);
  };

  const handleYearSelect = (year) => {
    setSelectedYear(year);
    setStep("month");
  };

  const handleCutoffYear = () => {
    setShowDynamo(true);
  };

  const handleMonthSelect = (monthIndex) => {
    const now = new Date();
    if (selectedYear === currentYear && monthIndex > now.getMonth()) return;
    setSelectedMonth(monthIndex);
    setStep("day");
  };

  const handleDaySelect = (day) => {
    const now = new Date();
    if (selectedYear === currentYear && selectedMonth === now.getMonth() && day > now.getDate()) return;
    setSelectedDay(day);
    setStep("postcode");
  };

  const handleFindClasses = async (skipPostcode = false) => {
    setIsLoading(true);
    const dob = `${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}-${String(selectedDay).padStart(2, "0")}`;
    try {
      const response = await axios.post(`${API}/class-finder`, {
        date_of_birth: dob,
        postcode: skipPostcode ? null : postcode || null
      });
      setResults(response.data);
      setStep("results");
      if (response.data.suitable_classes.length === 0) {
        toast.info("No classes available for this age right now.");
      } else {
        toast.success(`Found ${response.data.suitable_classes.length} class${response.data.suitable_classes.length > 1 ? "es" : ""}!`);
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const formatAgeRange = (min, max) => {
    if (min < 12) {
      const maxYrs = Math.floor(max / 12);
      return `${min} months - ${maxYrs} year${maxYrs > 1 ? "s" : ""}`;
    }
    return `${Math.floor(min / 12)} - ${Math.ceil(max / 12)} years`;
  };

  if (!isOpen) return null;

  const now = new Date();

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 animate-fade-in" data-testid="find-a-class-modal">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl relative max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#ff3131] to-[#e62e2e] px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            {(step !== "year" && !showDynamo) && (
              <button
                onClick={() => {
                  if (step === "month") setStep("year");
                  else if (step === "day") setStep("month");
                  else if (step === "postcode") setStep("day");
                  else if (step === "results") setStep("postcode");
                }}
                className="text-white/80 hover:text-white transition-colors"
                data-testid="back-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <h2 className="text-white font-bold text-lg" style={{ fontFamily: "Fredoka, cursive" }}>
              {step === "year" ? "When were they born?" :
               step === "month" ? "Which month?" :
               step === "day" ? "Which day?" :
               step === "postcode" ? "Your postcode" :
               "Classes for your child"}
            </h2>
          </div>
          <button onClick={handleClose} className="text-white/80 hover:text-white" data-testid="close-modal-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1">
          {/* Step: Year */}
          {step === "year" && (
            <div className="p-5 grid grid-cols-2 gap-2">
              {YEARS.map((y) => (
                <button
                  key={y}
                  onClick={() => handleYearSelect(y)}
                  className="py-3 rounded-xl text-base font-bold bg-[#FFFBF5] hover:bg-[#ff3131] hover:text-white text-[#2D3436] transition-all border-2 border-transparent hover:border-[#ff3131]"
                  style={{ fontFamily: "Fredoka, cursive" }}
                >
                  {y}
                </button>
              ))}
              <button
                onClick={handleCutoffYear}
                className="py-3 rounded-xl text-sm font-bold bg-[#7dbff8]/10 hover:bg-[#7dbff8] hover:text-white text-[#7dbff8] transition-all col-span-2 border-2 border-transparent hover:border-[#7dbff8]"
                style={{ fontFamily: "Fredoka, cursive" }}
                data-testid="cutoff-year-btn"
              >
                {CUTOFF_YEAR} & earlier
              </button>
            </div>
          )}

          {/* Step: Month */}
          {step === "month" && (
            <div className="p-5 grid grid-cols-3 gap-2">
              {MONTHS.map((m, i) => {
                const isFuture = selectedYear === currentYear && i > now.getMonth();
                return (
                  <button
                    key={m}
                    onClick={() => !isFuture && handleMonthSelect(i)}
                    disabled={isFuture}
                    className={`py-3 rounded-xl text-sm font-bold transition-all border-2 border-transparent ${
                      isFuture ? "opacity-30 cursor-not-allowed bg-gray-50 text-gray-400" :
                      "bg-[#FFFBF5] hover:bg-[#7dbff8] hover:text-white text-[#2D3436] hover:border-[#7dbff8]"
                    }`}
                    style={{ fontFamily: "Fredoka, cursive" }}
                  >
                    {m.slice(0, 3)}
                  </button>
                );
              })}
            </div>
          )}

          {/* Step: Day */}
          {step === "day" && selectedMonth !== null && selectedYear && (
            <div className="p-5 grid grid-cols-7 gap-1.5">
              {Array.from({ length: getDaysInMonth(selectedMonth, selectedYear) }, (_, i) => {
                const d = i + 1;
                const isFuture = selectedYear === currentYear && selectedMonth === now.getMonth() && d > now.getDate();
                return (
                  <button
                    key={d}
                    onClick={() => !isFuture && handleDaySelect(d)}
                    disabled={isFuture}
                    className={`py-2.5 rounded-xl text-sm font-bold transition-all ${
                      isFuture ? "opacity-30 cursor-not-allowed text-gray-400" :
                      "bg-[#FFFBF5] hover:bg-[#ff3131] hover:text-white text-[#2D3436]"
                    }`}
                    style={{ fontFamily: "Fredoka, cursive" }}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          )}

          {/* Step: Postcode */}
          {step === "postcode" && (
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-bold text-[#2D3436] mb-2" style={{ fontFamily: "Fredoka, cursive" }}>
                  Your Postcode <span className="text-xs font-normal text-[#636E72]">(optional)</span>
                </label>
                <input
                  type="text"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                  placeholder="e.g. SO31 4NE"
                  className="w-full rounded-xl border-2 border-gray-100 focus:border-[#7dbff8] p-3 text-base outline-none transition-all uppercase"
                  data-testid="postcode-input"
                  onKeyDown={(e) => e.key === "Enter" && handleFindClasses(false)}
                />
                <p className="text-xs text-[#636E72] mt-1">See which location is nearest to you</p>
              </div>
              <button
                onClick={() => handleFindClasses(false)}
                disabled={isLoading}
                className="w-full bg-[#ff3131] hover:bg-[#e62e2e] text-white rounded-full py-3.5 font-bold text-base flex items-center justify-center gap-2 transition-all disabled:opacity-60"
                style={{ fontFamily: "Fredoka, cursive" }}
                data-testid="find-classes-btn"
              >
                {isLoading ? (
                  <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Searching...</>
                ) : (
                  <><Search className="w-5 h-5" /> Find Classes</>
                )}
              </button>
              <button
                onClick={() => handleFindClasses(true)}
                disabled={isLoading}
                className="w-full text-[#636E72] text-sm mt-2 py-2 hover:text-[#ff3131] transition-colors underline"
                data-testid="skip-postcode-btn"
              >
                Skip, just show me classes
              </button>
            </div>
          )}

          {/* Step: Results */}
          {step === "results" && results && (
            <div className="p-5">
              <div className="flex items-center justify-center gap-2 bg-[#FFFBF5] rounded-xl p-3 mb-4">
                <Baby className="w-4 h-4 text-[#7dbff8]" />
                <span className="text-sm text-[#2D3436]">
                  Your child is <span className="text-[#ff3131] font-bold">{results.age_display}</span> old
                </span>
              </div>

              {results.suitable_classes.length === 0 ? (
                <div className="text-center py-6">
                  <h3 className="text-lg font-bold text-[#2D3436] mb-2" style={{ fontFamily: "Fredoka, cursive" }}>
                    No classes available
                  </h3>
                  <p className="text-sm text-[#636E72] mb-4">
                    We don't currently have classes for this age. Contact us and we'll let you know when new classes open!
                  </p>
                  <a href="tel:02380455007" className="inline-block bg-[#ff3131] hover:bg-[#e62e2e] text-white px-6 py-2.5 rounded-full font-bold text-sm" style={{ fontFamily: "Fredoka, cursive" }}>
                    Call Us
                  </a>
                </div>
              ) : (
                <div className="space-y-3">
                  {results.suitable_classes.map((cls) => (
                    <div key={cls.id} className="bg-[#FFFBF5] rounded-xl p-4 border border-gray-100" data-testid={`class-result-${cls.id}`}>
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h4 className="font-bold text-[#2D3436]" style={{ fontFamily: "Fredoka, cursive" }}>
                          {cls.class_name}
                        </h4>
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#7dbff8] text-white">
                          £{cls.price.toFixed(2)}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-2 text-xs text-[#636E72] mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-[#7dbff8]" />
                          {cls.day_of_week} {cls.time}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-[#7dbff8]" />
                          {cls.duration_minutes} mins
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3 text-[#7dbff8]" />
                          {cls.with_parent !== false ? "With parent" : "Independent"}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-[#7dbff8]" />
                          {cls.location_display}
                          {cls.distance_miles != null && (
                            <span className="text-[#ff3131] font-semibold ml-0.5">({cls.distance_miles} mi)</span>
                          )}
                        </span>
                      </div>

                      <a
                        href={getBookingUrl(bookingLinks, cls.class_type)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-[#ff3131] hover:bg-[#e62e2e] text-white py-2.5 rounded-full font-bold text-sm transition-colors flex items-center justify-center gap-2"
                        style={{ fontFamily: "Fredoka, cursive" }}
                        data-testid={`book-now-${cls.id}`}
                      >
                        Book a Trial Session!
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  ))}
                </div>
              )}

              <button onClick={reset} className="w-full text-[#636E72] text-sm mt-4 py-2 hover:text-[#ff3131] transition-colors underline" data-testid="search-again-btn">
                Search again
              </button>
            </div>
          )}
        </div>

        {/* Dynamo Popup */}
        {showDynamo && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-[#1656a4] rounded-2xl p-8 animate-pop-in">
            <div className="text-center">
              <img src={siteImages["dynamo-logo-cropped.png"] || ""} alt="Dynamo Gymnastics" className="h-20 w-auto mx-auto mb-6" />
              <h3 className="text-white text-xl font-bold mb-3" style={{ fontFamily: "Fredoka, cursive" }}>
                Your child may be ready for bigger things!
              </h3>
              <p className="text-white/90 text-sm mb-6 leading-relaxed">
                Based on your child's age, please contact <strong>Dynamo Gymnastics</strong> for classes suitable for them.
              </p>
              <a href="mailto:admin@dynamogymnastics.co.uk?subject=Class%20Enquiry" className="inline-block bg-white text-[#1656a4] px-6 py-3 rounded-full font-bold text-sm hover:bg-gray-100 mb-3" style={{ fontFamily: "Fredoka, cursive" }} data-testid="dynamo-email-btn">
                Email Dynamo Gymnastics
              </a>
              <br />
              <button onClick={() => { setShowDynamo(false); reset(); }} className="text-white/70 text-sm mt-2 hover:text-white underline" data-testid="dynamo-back-btn">
                Go back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FindAClassModal;
